import * as z from 'zod';

/**
 * What the SSO form accepts, and what it hands to `signInWithSSO`.
 *
 * People type their work email address, because that is what every other field
 * on this screen asks for. GoTrue routes by DOMAIN. So the field takes either —
 * `someone@bank.ae` or `bank.ae` — and this schema is where the one becomes the
 * other, once, rather than in the component and again in a test.
 *
 * The domain is folded to lower case for the reason `sso_domain_mappings` folds
 * it: `auth.sso_domains` is uniquely indexed on `lower(domain)`, so a
 * capitalised entry would simply fail to route, and "Bank.ae is not registered"
 * is a confusing thing to tell someone who works at bank.ae.
 */
export const SsoSignInSchema = z
  .object({
    identifier: z
      .string()
      .trim()
      .min(1, 'Enter your work email address or your company domain'),
  })
  .transform(({ identifier }) => ({
    domain: extractDomain(identifier),
  }))
  .refine(({ domain }) => DOMAIN.test(domain), {
    path: ['identifier'],
    error: 'That does not look like an email address or a domain',
  });

export type SsoSignInInput = z.output<typeof SsoSignInSchema>;

/**
 * At least two labels, so `bank` alone is refused.
 *
 * A single label is refused rather than accepted-and-looked-up because it can
 * never match: `auth.sso_domains` holds real domains, so `bank` would come back
 * "no provider" — the same message an unregistered company gets, which is the
 * one message that must stay meaningful.
 */
const DOMAIN =
  /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

/**
 * `a@b.com` -> `b.com`; `b.com` -> `b.com`.
 *
 * `split('@').pop()` rather than `split('@')[1]`, so an address that somehow
 * carries two `@` yields the last segment instead of a middle one — the same
 * segment a mail system would route on.
 */
function extractDomain(value: string) {
  const folded = value.trim().toLowerCase();

  return folded.includes('@') ? (folded.split('@').pop() ?? '') : folded;
}
